/**
 * Horizontal card carousels (.ds-carousel).
 *
 * This used to live in discover.js, which the layout loads only for the discover
 * and collections controllers — so the arrows on the homepage, the blog hubs and
 * the gallery rendered but bound to nothing. The tracks are `overflow-x: auto`,
 * so they still scrolled by trackpad or swipe and nobody noticed the buttons were
 * dead. It lives here now and is imported by application.js.
 *
 * The nav lives outside the root when a section renders its arrows in the header
 * (see `data-carousel-nav`), so buttons are looked up inside the root first and
 * then in the associated nav.
 */
export function initializeCarousels(scope = document) {
  const carousels = scope.querySelectorAll('[data-carousel-root]');
  if (!carousels.length) return;

  carousels.forEach((carousel) => {
    if (carousel.dataset.carouselInitialized === 'true') return;

    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;

    const externalNav = carousel.id
      ? document.querySelector(`[data-carousel-nav="${carousel.id}"]`)
      : null;

    const findButton = (direction) =>
      carousel.querySelector(`[data-carousel-button="${direction}"]`) ||
      (externalNav && externalNav.querySelector(`[data-carousel-button="${direction}"]`));

    const prevButton = findButton('prev');
    const nextButton = findButton('next');

    const updateScrollState = () => {
      const maxScrollLeft = track.scrollWidth - track.clientWidth - 1;
      const atStart = track.scrollLeft <= 1;
      // A track that doesn't overflow is at both ends at once: disable both arrows
      // rather than leave "next" live with nowhere to go.
      const atEnd = track.scrollLeft >= maxScrollLeft;

      carousel.dataset.scrollAtStart = atStart ? 'true' : 'false';
      carousel.dataset.scrollAtEnd = atEnd ? 'true' : 'false';

      if (prevButton) prevButton.disabled = atStart;
      if (nextButton) nextButton.disabled = atEnd;
    };

    const scrollByAmount = (direction) => {
      const firstItem = track.querySelector('[data-carousel-item]');
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : track.clientWidth * 0.8;
      const delta = direction === 'next' ? itemWidth + gap : -(itemWidth + gap);

      track.scrollBy({ left: delta, behavior: 'smooth' });
    };

    if (prevButton) {
      prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        scrollByAmount('prev');
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        scrollByAmount('next');
      });
    }

    track.addEventListener('scroll', updateScrollState, { passive: true });

    // ResizeObserver rather than a window listener, so a removed carousel doesn't
    // leak a handler.
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateScrollState());
      resizeObserver.observe(track);
    }

    updateScrollState();

    carousel.dataset.carouselInitialized = 'true';
    carousel._cleanup = () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  });
}

function boot() {
  initializeCarousels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// The app navigates with Turbolinks; `carouselInitialized` guards double-binding.
document.addEventListener('turbolinks:load', boot);
document.addEventListener('turbo:load', boot);

export default initializeCarousels;
